import React from 'react';
import Header from '../../components/UI/Header';
import HeroSection from './components/HeroSection';
import AboutMeLanding from './components/AboutMeLanding';
import ExperienceTimeline from './components/ExperienceTimeline';
import FeaturedProjects from './components/FeaturedProjects';
import TestimonialsCarousel from './components/TestimonialsCarousel';
import ContactSection from './components/ContactSection';
import WaveDivider from './components/WaveDivider';
import {
  PersonalInfo,
  Skill,
  Experience,
  FeaturedProject,
  Testimonial,
  SocialLink,
  ContactInfo
} from
  './types';
import Icon from '../../components/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';


import { useContent } from '../../hooks/useContent';
import { useProjects } from '../../hooks/useProjects';
import { useGitHub } from '../../hooks/useGitHub';
import Footer from '../../components/UI/Footer';

import {
  defaultPersonalInfo,
  defaultSkills,
  defaultExperiences,
  defaultTestimonials,
  defaultSocialLinks,
  defaultContactInfo
} from './constants/defaults';

const HomeLanding = () => {
  // Fetch Data
  const { content: personalInfoData } = useContent<PersonalInfo>('hero', defaultPersonalInfo);
  const { content: contactInfo } = useContent<ContactInfo>('contact', defaultContactInfo);
  const { content: skills } = useContent<Skill[]>('skills', defaultSkills);
  const { content: experiences } = useContent<Experience[]>('experience', defaultExperiences);
  const { content: testimonials } = useContent<Testimonial[]>('testimonials', defaultTestimonials);
  const { content: socialLinks } = useContent<SocialLink[]>('social_links', defaultSocialLinks);
  const { content: resumes } = useContent<any[]>('about_resume', []);

  const { recentCommits, totalCommits, loading: githubLoading } = useGitHub(personalInfoData?.githubUsername);
  const { projects: dbProjects, loading: projectsLoading } = useProjects();

  // Determine the primary resume from backend, fallback to default profile resume
  const primaryResumeUrl = resumes && resumes.length > 0 ? resumes[0].url : personalInfoData?.resumeUrl;

  const { content: education } = useContent<any[]>('about_education', []);
  const { content: achievements } = useContent<any[]>('about_achievements', []);

  const personalInfo: PersonalInfo = {
    ...personalInfoData,
    resumeUrl: primaryResumeUrl || '',
  };

  // Format commit count (e.g., 1200 -> 1.2k)
  const formatCount = (num: number) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  const heroStats = {
    projectsCount: dbProjects.length,
    yearsOfExperience: personalInfoData?.yearsOfExperience || 0,
    githubCommits: totalCommits > 0 ? `${formatCount(totalCommits)}+` : (personalInfo.githubCommits || '0'),
    happyClients: personalInfo.happyClients || '0',
    educationCount: Array.isArray(education) ? education.length : 0,
    achievementCount: Array.isArray(achievements) ? achievements.length : 0,
    latestCommit: recentCommits[0]
  };

  const featuredProjects: FeaturedProject[] = dbProjects
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .map(p => {
      const details = p.details || {};
      return {
        id: p.id,
        title: p.title || 'Operational Module',
        description: p.description || 'System specifications pending disclosure.',
        image: p.image_url || '/image/project-placeholder.jpg',
        imageAlt: p.title || 'Project specification',
        technologies: Array.isArray(p.tags) ? p.tags : [],
        liveUrl: p.live_link || '',
        githubUrl: p.github_link || '',
        demoCredentials: details.demoCredentials
      };
    });

  return (
    <div className="min-h-screen bg-transparent">
      <Header />

      <main className="relative z-10 w-full">
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <HeroSection personalInfo={personalInfo} stats={heroStats} />

            {/* Transition to Skills (Dark Teal) */}
            <WaveDivider color="#0C2C39" className="-mb-px" />

            <section className="bg-[#0C2C39]">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <AboutMeLanding personalInfo={personalInfo} stats={heroStats} />
              </motion.div>

              {/* Transition to Experience (Cream) */}
              <WaveDivider color="#0C2C39" flip className="-mt-px" />
            </section>

            <section className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <ExperienceTimeline
                  experiences={experiences}
                  resumeUrl={personalInfo.resumeUrl}
                  contactEmail={contactInfo.email}
                  personalInfo={personalInfo}
                />
              </motion.div>
            </section>

            {/* Transition to Projects (Dark Teal) */}
            <WaveDivider color="#0C2C39" className="-mb-px" />

            <section className="bg-[#0C2C39] py-24">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <FeaturedProjects projects={featuredProjects} personalInfo={personalInfo} />
              </motion.div>

              {/* Transition to Testimonials (Cream) - Flip Teal Wave */}
              <WaveDivider color="#0C2C39" flip className="-mt-px" />
            </section>

            <section className="py-24">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <TestimonialsCarousel testimonials={testimonials} personalInfo={personalInfo} />
              </motion.div>
            </section>

            {/* Transition to Contact (Dark Teal) */}
            <WaveDivider color="#0C2C39" className="-mb-px pb-px" />

            <section className="bg-[#0C2C39] pb-32">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <ContactSection contactInfo={contactInfo} socialLinks={socialLinks} personalInfo={personalInfo} />
              </motion.div>
            </section>
          </motion.div>
        </AnimatePresence>
      </main>

      <Footer socialLinks={socialLinks} />
    </div>);

};

export default HomeLanding;